import { Box, Grid, InfoOutlineIcon } from 'src/ui'
import { transformGroupNameToTitleCase } from 'src/utils/tenant-configuration'
import { FormControl, FormLabel } from 'src/ui/form-controls'
import { representValue } from 'src/companies/configurations/utils'
import React from 'react'
import { Code, Switch } from '@chakra-ui/react'
import { JsonSchemaTooltip } from 'src/components/JsonSchemaTooltip'

interface ConfigurationPanelDetailProps {
  tenantConfig: { [key: string]: TenantConfiguration[] }
  encryptedKeys?: string[]
}

export default function ConfigurationPanelDetail({
  tenantConfig,
  encryptedKeys = [],
}: Readonly<ConfigurationPanelDetailProps>) {
  return (
    <>
      {Object.entries(tenantConfig).map(([groupName, groupConfigs], groupIndex) => (
        <Box bg={groupIndex % 2 === 1 ? 'gray.50' : undefined} key={groupName} px={6}>
          <Box fontWeight="bold" py={4}>
            {transformGroupNameToTitleCase(groupName)}
          </Box>
          <Grid templateColumns={`repeat(auto-fit, minmax(300px, 1fr))`} columnGap={4}>
            {groupConfigs.map((config: TenantConfiguration) => (
              <Box
                key={config.key}
                display="flex"
                alignItems="flex-start"
                flex="1"
                minWidth="0"
                py={2}
              >
                <Box>
                  <FormControl height="unset" minHeight="72px" mb={null}>
                    <FormLabel as="div" title="Label">
                      {config.label}
                      <JsonSchemaTooltip config={config} placement="top" hasArrow>
                        <InfoOutlineIcon alignSelf="center" ml="1" color="gray.400" />
                      </JsonSchemaTooltip>
                    </FormLabel>
                    <Box
                      flex={1}
                      minW={0}
                      minHeight={8}
                      color={config.value === 'null' ? 'gray.400' : undefined}
                    >
                      <ConfigurationPanelDetailItem config={config} encryptedKeys={encryptedKeys} />
                    </Box>
                  </FormControl>
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>
      ))}
    </>
  )
}

function ConfigurationPanelDetailItem({
  config,
  encryptedKeys = [],
}: Readonly<{ config: TenantConfiguration; encryptedKeys?: string[] }>) {
  const isEncryptedField = encryptedKeys.includes(config.key) && !!config.value
  const isJson = config.dataType.toLowerCase() === 'json'
  const isBoolean = config.dataType.toLowerCase() === 'boolean'

  if (isEncryptedField) {
    return (
      <Box as="span" fontStyle="italic">
        &lt;encrypted&gt;
      </Box>
    )
  }

  if (isBoolean) {
    return <Switch isChecked={config.value === 'true'} disabled />
  }

  if (isJson) {
    return (
      <Code bgColor="inherit" p={2} as="pre" fontSize="xs" overflowX="auto" whiteSpace="pre-wrap">
        {JSON.stringify(config.value, undefined, 2)}
      </Code>
    )
  }

  return (
    <Box whiteSpace="pre-wrap" wordBreak="break-word" overflowWrap="anywhere" maxW="100%">
      {representValue(config.value)}
    </Box>
  )
}
